import React, { useCallback, useEffect, useState } from 'react';
import Avatar from './components/Avatar';
import MenuList from './components/MenuList';
import Cart from './components/Cart';
import Receipt from './components/Receipt';
import PinLock from './components/PinLock';
import { api, getSessionId } from './api';
import { useSpeechRecognition, useSpeechSynthesis } from './hooks/useVoice';
import './App.css';

const SID = getSessionId();

const LANGS = [
  { code: 'en-US', label: 'EN', flag: '🇺🇸' },
  // { code: 'hi-IN', label: 'HI', flag: '🇮🇳' },
  // { code: 'zh-CN', label: 'ZH', flag: '🇨🇳' },
];

const GREETINGS = {
  'en-US': "Hi there! I'm Aina, your waitress today. Tap the mic and tell me what you'd like to order.",
  'hi-IN': 'नमस्ते! मैं आइना हूँ, आज आपकी वेट्रेस। माइक दबाइए और बताइए कि आप क्या ऑर्डर करना चाहेंगे।',
  'zh-CN': '你好！我是艾娜，今天为您服务。请点击麦克风告诉我您想点些什么。',
};

export default function App() {
  const [unlocked, setUnlocked] = useState(false);
  const [menu, setMenu] = useState([]);
  const [cart, setCart] = useState({ items: [] });
  const [messages, setMessages] = useState([]);
  const [lang, setLang] = useState(() => localStorage.getItem('ainatalk_lang') || 'en-US');
  useEffect(() => localStorage.setItem('ainatalk_lang', lang), [lang]);
  const [lastReply, setLastReply] = useState(GREETINGS[lang] || GREETINGS['en-US']);
  // When switching language before any chat has happened, update the default greeting shown
  useEffect(() => {
    setLastReply((prev) =>
      Object.values(GREETINGS).includes(prev) ? GREETINGS[lang] : prev
    );
  }, [lang]);
  const [thinking, setThinking] = useState(false);
  const [error, setError] = useState('');
  const [receipt, setReceipt] = useState(null);

  const { speak, cancel, speaking } = useSpeechSynthesis();

  // Body-language state: which animation clip Aina is currently playing.
  // Reset back to Idle whenever she finishes speaking.
  const [animation, setAnimation] = useState('Idle');
  useEffect(() => {
    if (!speaking) setAnimation('Idle');
  }, [speaking]);

  // Wraps speak() with a gesture that plays for the duration of the reply.
  const speakWithGesture = useCallback(
    (text, gesture) => {
      setAnimation(gesture);
      speak(text);
    },
    [speak]
  );

  const sendMessage = useCallback(
    async (text) => {
      if (!text) return;
      setError('');
      setThinking(true);
      setMessages((m) => [...m, { role: 'user', content: text }]);
      try {
        const history = messages.slice(-6);
        const res = await api.chat(SID, text, history);
        setCart(res.cart);
        setLastReply(res.reply);
        setMessages((m) => [...m, { role: 'assistant', content: res.reply }]);
        const placed = !!res.order;
        const cartChanged = Array.isArray(res.actions) && res.actions.length > 0;
        const chatty = ['Talking_0', 'Talking_1'][Math.floor(Math.random() * 2)];
        speakWithGesture(res.reply, placed ? 'Laughing' : cartChanged ? 'Talking_2' : chatty);
        // If Aina placed the order via voice, run the same print flow as the button
        if (placed) {
          setTimeout(() => setReceipt(res.order), 400);
        }
      } catch (e) {
        setError('Sorry, something went wrong talking to Aina.');
      } finally {
        setThinking(false);
      }
    },
    [messages, speakWithGesture]
  );

  const { start, stop, setPaused, listening, interim, supported } = useSpeechRecognition({
    onResult: (text) => sendMessage(text),
    lang,
  });

  // Pause mic while Aina is speaking so she doesn't hear herself
  useEffect(() => {
    setPaused(speaking);
  }, [speaking, setPaused]);

  useEffect(() => {
    (async () => {
      try {
        const [m, c] = await Promise.all([api.menu(), api.cart(SID)]);
        setMenu(m);
        setCart(c);
      } catch (_) {
        setError('Cannot reach the server. Is the backend running on :5151?');
      }
    })();
  }, []);

  const greet = useCallback(
    () => speakWithGesture(GREETINGS[lang], 'Laughing'),
    [speakWithGesture, lang]
  );

  async function addManual(item) {
    const c = await api.addToCart(SID, item._id, 1);
    setCart(c);
    const reply = `Added ${item.name} to your order.`;
    setLastReply(reply);
    speakWithGesture(reply, 'Talking_2');
  }

  async function removeManual(id) {
    const c = await api.removeFromCart(SID, id);
    setCart(c);
  }

  async function clearAll() {
    const c = await api.clearCart(SID);
    setCart(c);
    const reply = 'Cart cleared. What would you like instead?';
    setLastReply(reply);
    speakWithGesture(reply, 'Talking_2');
  }

  async function checkout() {
    if (!cart.items?.length) return;
    const snapshot = {
      items: cart.items,
      total: cart.items.reduce((s, l) => s + l.price * l.quantity, 0),
      when: new Date().toLocaleString(),
    };
    const reply = "Great! Your order is placed. It'll be right out.";
    setLastReply(reply);
    speakWithGesture(reply, 'Laughing');

    // Clear items first — the cart visually empties…
    const c = await api.clearCart(SID);
    setCart(c);
    // …then the printer starts a short beat later.
    setTimeout(() => setReceipt(snapshot), 400);
  }

  // Toggle: tap once to start continuous listening, tap again to stop
  const [micOn, setMicOn] = useState(false);
  const toggleMic = () => {
    if (speaking) cancel();
    if (micOn) {
      stop();
      setMicOn(false);
    } else {
      start();
      setMicOn(true);
    }
  };

  if (!unlocked) {
    return <PinLock onUnlock={() => setUnlocked(true)} />;
  }

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">🍽️ Aina's Diner</div>
        <div className="sub">Talk to your waitress — she'll take your order</div>
        {LANGS.length > 1 && (
          <div className="lang-picker">
            {LANGS.map((l) => (
              <button
                key={l.code}
                className={lang === l.code ? 'on' : ''}
                onClick={() => setLang(l.code)}
                title={l.code}
              >
                <span className="flag">{l.flag}</span> {l.label}
              </button>
            ))}
          </div>
        )}
      </header>

      <main className="grid">
        {/* LEFT — menu */}
        <section className="pane left">
          <h2 className="pane-title">Menu</h2>
          <MenuList items={menu} onAdd={addManual} />
        </section>

        {/* MIDDLE — avatar shop scene */}
        <section className="pane middle">
          <div className="chalkboard">
            <h4>Today's Specials</h4>
            <ul>
              <li>Margherita Pizza</li>
              <li>Cheeseburger</li>
              <li>Caesar Salad</li>
              <li>Chocolate Cake</li>
            </ul>
          </div>
          <div className="register" />

          <Avatar speaking={speaking} listening={listening} animation={animation} />

          <div className="bubble">
            {interim ? <em>{interim}…</em> : lastReply}
          </div>

          <div className="voice-controls">
            <button
              className={`mic ${micOn ? 'on' : ''} ${speaking ? 'speaking' : ''}`}
              onClick={toggleMic}
              disabled={!supported}
              title={supported ? 'Tap to start / stop listening' : 'Speech recognition not supported in this browser'}
            >
              {micOn
                ? speaking
                  ? '🔊 Speaking… (mic paused)'
                  : listening
                  ? '● Listening — Tap to Stop'
                  : '… Tap to Stop'
                : '🎤 Tap to Talk'}
            </button>
            <button className="btn-ghost" onClick={greet}>Say hi</button>
          </div>

          {/* Siri-style waveform — only visible while the mic is actively listening */}
          {listening && !speaking && (
            <div className="listen-wave" aria-hidden="true">
              {Array.from({ length: 9 }).map((_, i) => (
                <span key={i} style={{ animationDelay: `${i * 90}ms` }} />
              ))}
              <div className="listen-label">Listening…</div>
            </div>
          )}

          {!supported && (
            <div className="hint">
              Voice input needs Chrome or Edge. You can still tap items to add them.
            </div>
          )}
          {error && <div className="error">{error}</div>}
        </section>

        {/* RIGHT — cart + printed receipt */}
        <section className="pane right">
          <Cart
            cart={cart}
            onRemove={removeManual}
            onClear={clearAll}
            onCheckout={checkout}
          />
          <Receipt order={receipt} onClose={() => setReceipt(null)} />
        </section>
      </main>
    </div>
  );
}
